import { ChevronDownIcon, PlusIcon } from "lucide-react";
import { useId, useState } from "react";
import { type Control, type FieldPath, useController, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Checkbox } from "../../../../../components/ui/uikit/checkbox";
import { DateInput } from "../../../../../components/ui/uikit/dateInput";
import { Input } from "../../../../../components/ui/uikit/input";
import { cn } from "../../../../../lib/utils";
import {
  CITY_OPTIONS,
  HOLD_COUPON_BOOK_OPTIONS,
  MAX_PHONES_PER_PERSON,
  SUFFIX_OPTIONS,
  US_STATE_OPTIONS,
} from "../constants";
import { useFormReadOnly } from "../readOnlyContext";
import { getSurfacedSelect } from "../selectSurfacing";
import type { CustomerInfoInput, SignersInput } from "../Signers.schema";
import { makeEmptyPhone } from "../Signers.schema";
import { PhoneRow } from "./PhoneRow";
import { SsnInput } from "./SsnInput";
import { useMirrorAddress } from "./useMirrorAddress";
import { useZipAutofill } from "./useZipAutofill";
import { ZipLookupMessage } from "./ZipLookupMessage";

// ---------------------------------------------------------------------------
// Radix Select sentinel — Radix requires a non-empty string value; map "" ↔ this
// ---------------------------------------------------------------------------
const NONE_SENTINEL = "__none__";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface CustomerInformationProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  /** Ref callback registering the whole Customer Information block (the "Residence" section) with the Signers-level scroll-spy. */
  residenceSectionRef?: (element: HTMLElement | null) => (() => void) | void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getCustomerFieldPath(personPath: PersonPath, field: keyof CustomerInfoInput): FieldPath<SignersInput> {
  return `${personPath}.customerInfo.${field}` as FieldPath<SignersInput>;
}

// ---------------------------------------------------------------------------
// Required marker — visual only (no validation enforcement in this story)
// ---------------------------------------------------------------------------
function RequiredMarker() {
  return (
    <span className="text-destructive ml-0.5" aria-hidden="true">
      *
    </span>
  );
}

// ---------------------------------------------------------------------------
// SsnField — labelled 3-part SSN/ITIN split input wired to RHF via useController.
// Stores a canonical digits string; validation lives in customerInfoSchema.
// ---------------------------------------------------------------------------
interface SsnFieldProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  /**
   * Lock the field. Overridden while the value carries a validation error —
   * customerInfoSchema rejects a malformed SSN/ITIN and signersSchema rejects a
   * driver/2nd-signer duplicate, and both of those can arrive from upstream, so
   * a permanently locked field would block Save with no way out of it.
   */
  disabled?: boolean;
}

function SsnField({ control, personPath, disabled }: SsnFieldProperties) {
  const { t } = useTranslation();
  const uid = useId();
  const { field, fieldState } = useController({
    control,
    name: getCustomerFieldPath(personPath, "ssnOrItin"),
  });
  const label = t("review.customer.personal.ssnOrItin");
  const errorId = `${uid}-ssn-error`;

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium">
        {label}
        <RequiredMarker />
      </label>
      <SsnInput
        idBase={`${uid}-ssn`}
        value={field.value == null ? "" : String(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        ariaLabel={label}
        required
        invalid={Boolean(fieldState.error)}
        disabled={disabled && !fieldState.error}
      />
      {fieldState.error && (
        <p id={errorId} className="text-destructive text-2xs" role="alert">
          {fieldState.error.message}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleTextField — a labelled text input wired to RHF via useController
// ---------------------------------------------------------------------------
interface SimpleTextFieldProperties {
  control: Control<SignersInput>;
  fieldPath: FieldPath<SignersInput>;
  label: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Hide the visible label (label is exposed via aria-label instead). Used by grouped fields like Name. */
  hideLabel?: boolean;
  /** Pass "off" for PII inputs (SSN/ITIN, DOB, Driver License) to prevent browser caching. */
  autoComplete?: string;
  /** Force the invalid (red-border) state independent of RHF validation — e.g. a failed ZIP lookup. */
  invalid?: boolean;
}

function SimpleTextField({
  control,
  fieldPath,
  label,
  required,
  placeholder,
  disabled,
  className,
  hideLabel,
  autoComplete,
  invalid,
}: SimpleTextFieldProperties) {
  const uid = useId();
  const inputId = `${uid}-input`;
  const { field } = useController({ control, name: fieldPath });

  const input = (
    <Input
      id={inputId}
      type="text"
      value={field.value == null ? "" : String(field.value)}
      onChange={field.onChange}
      onBlur={field.onBlur}
      ref={field.ref}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      aria-required={required}
      aria-invalid={invalid || undefined}
      aria-label={hideLabel ? label : undefined}
      autoComplete={autoComplete}
    />
  );

  if (hideLabel) {
    return input;
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
        {required && <RequiredMarker />}
      </label>
      {input}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleDateField — a labelled date picker wired to RHF via useController
// ---------------------------------------------------------------------------
interface SimpleDateFieldProperties {
  control: Control<SignersInput>;
  fieldPath: FieldPath<SignersInput>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Mirrors SimpleTextField, but renders the uikit date picker. The form value
 * stays ISO (`YYYY-MM-DD`) — the calculator compares Date of Birth against the
 * hire and pay dates with plain string operators, so the stored shape has to
 * keep sorting lexicographically.
 */
function SimpleDateField({ control, fieldPath, label, required, disabled, className }: SimpleDateFieldProperties) {
  const uid = useId();
  const inputId = `${uid}-input`;
  const { field } = useController({ control, name: fieldPath });

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-xs font-medium">
        {label}
        {required && <RequiredMarker />}
      </label>
      <DateInput
        id={inputId}
        value={field.value == null ? "" : String(field.value)}
        onChange={field.onChange}
        onBlur={field.onBlur}
        disabled={disabled}
        className={className}
        aria-required={required}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SimpleSelectField — a labelled select wired to RHF via useController
// ---------------------------------------------------------------------------
interface SimpleSelectFieldProperties {
  control: Control<SignersInput>;
  fieldPath: FieldPath<SignersInput>;
  label: string;
  required?: boolean;
  options: ReadonlyArray<{ value: string; label: string }>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Hide the visible label (label is exposed via aria-label instead). Used by grouped fields like Name. */
  hideLabel?: boolean;
}

function SimpleSelectField({
  control,
  fieldPath,
  label,
  required,
  options,
  disabled,
  placeholder,
  className,
  hideLabel,
}: SimpleSelectFieldProperties) {
  const uid = useId();
  const selectId = `${uid}-select`;
  const { field } = useController({ control, name: fieldPath });
  // Radix Select ignores the enclosing disabled <fieldset>; fold the lock in
  // alongside any caller-supplied `disabled` (e.g. mailing "same as current").
  const readOnly = useFormReadOnly();
  const isDisabled = disabled || readOnly;

  const currentValue = field.value == null ? "" : String(field.value);
  // Use shared surfacing helper: surfaces an out-of-options value and computes
  // the remount key so Radix Select can display it from the first render.
  const { resolvedOptions, selectKey } = getSurfacedSelect(currentValue, options);

  // Radix Select requires a non-empty string value; map "" to NONE_SENTINEL
  // so the placeholder renders and the field controller receives "" on change.
  const radixValue = currentValue === "" ? NONE_SENTINEL : currentValue;

  function handleValueChange(v: string) {
    field.onChange(v === NONE_SENTINEL ? "" : v);
  }

  const select = (
    <Select key={selectKey} value={radixValue} onValueChange={handleValueChange} disabled={isDisabled}>
      <SelectTrigger id={selectId} className={className} aria-label={label} disabled={isDisabled}>
        <SelectValue placeholder={placeholder ?? label} />
      </SelectTrigger>
      <SelectContent>
        {resolvedOptions.map((opt) => (
          <SelectItem
            key={opt.value === "" ? NONE_SENTINEL : opt.value}
            value={opt.value === "" ? NONE_SENTINEL : opt.value}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (hideLabel) {
    return select;
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={selectId} className="text-xs font-medium">
        {label}
        {required && <RequiredMarker />}
      </label>
      {select}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddressFields — reusable address group (Street No / Street Name / Apt + Zip / City / State)
// ---------------------------------------------------------------------------
interface AddressFieldsProperties {
  control: Control<SignersInput>;
  streetNumberPath: FieldPath<SignersInput>;
  streetNamePath: FieldPath<SignersInput>;
  aptPath: FieldPath<SignersInput>;
  zipPath: FieldPath<SignersInput>;
  cityPath: FieldPath<SignersInput>;
  statePath: FieldPath<SignersInput>;
  isPhysical?: boolean;
  disabled?: boolean;
}

function AddressFields({
  control,
  streetNumberPath,
  streetNamePath,
  aptPath,
  zipPath,
  cityPath,
  statePath,
  isPhysical,
  disabled,
}: AddressFieldsProperties) {
  const { t } = useTranslation();

  // Suppress autofill while the block is disabled (e.g. mailing when "Same as
  // current" is on — the physical autofill + mirror effect already covers that).
  const { status: zipStatus, invalid: zipInvalid } = useZipAutofill({
    zipPath,
    cityPath,
    statePath,
    enabled: !disabled,
  });

  return (
    <div className="flex flex-col gap-2">
      {/* Address: single label, placeholder-only Street No. (2) | Street Name (8) | Apt. # (2) */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium">
          {t("review.customer.residence.address")}
          {isPhysical && <RequiredMarker />}
        </span>
        <div className="grid grid-cols-1 items-start gap-x-3 gap-y-2 sm:grid-cols-12">
          <div className="sm:col-span-2">
            <SimpleTextField
              control={control}
              fieldPath={streetNumberPath}
              label={t("review.customer.residence.streetNumber")}
              placeholder={t("review.customer.residence.streetNumber")}
              required={isPhysical}
              disabled={disabled}
              hideLabel
              className="w-full"
            />
          </div>
          <div className="sm:col-span-8">
            <SimpleTextField
              control={control}
              fieldPath={streetNamePath}
              label={t("review.customer.residence.streetName")}
              placeholder={t("review.customer.residence.streetName")}
              required={isPhysical}
              disabled={disabled}
              hideLabel
              className="w-full"
            />
          </div>
          <div className="sm:col-span-2">
            <SimpleTextField
              control={control}
              fieldPath={aptPath}
              label={t("review.customer.residence.apt")}
              placeholder={t("review.customer.residence.apt")}
              disabled={disabled}
              hideLabel
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Zip | City | State */}
      <div className="grid grid-cols-1 items-start gap-x-3 gap-y-2 sm:grid-cols-12">
        <div className="sm:col-span-6 lg:col-span-3">
          <SimpleTextField
            control={control}
            fieldPath={zipPath}
            label={t("review.customer.residence.zip")}
            required={isPhysical}
            disabled={disabled}
            invalid={zipInvalid}
          />
          <ZipLookupMessage status={zipStatus} />
        </div>
        <div className="sm:col-span-6 lg:col-span-5">
          <SimpleSelectField
            control={control}
            fieldPath={cityPath}
            label={t("review.customer.residence.city")}
            options={CITY_OPTIONS}
            disabled={disabled}
          />
        </div>
        <div className="sm:col-span-12 lg:col-span-4">
          <SimpleSelectField
            control={control}
            fieldPath={statePath}
            label={t("review.customer.residence.state")}
            options={US_STATE_OPTIONS}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CustomerInformation
//
// Per-person collapsible section with three static sub-sections:
//   A. Personal Details
//   B. Residence Information (Physical + Mailing with "Same as current" mirror)
//   C. Contact Information (phones field array + email with "Email Not Available")
//
// Collapse state is block-local (UI concern only — mirrors BankInformation pattern).
// ---------------------------------------------------------------------------
export function CustomerInformation({ control, personPath, residenceSectionRef }: CustomerInformationProperties) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  // Unique id per mount prevents aria-controls collisions when both driver and
  // secondSigner columns are rendered in the same document.
  const uid = useId();
  const contentId = `${personPath}-customer-info-${uid}-content`;

  // ---------------------------------------------------------------------------
  // Phones field array — scoped inside CustomerInformation (no external consumer)
  // ---------------------------------------------------------------------------
  const phonesName = `${personPath}.customerInfo.phones` as
    | `driver.customerInfo.phones`
    | `secondSigner.customerInfo.phones`;
  const { fields: phoneFields, append, remove } = useFieldArray({ control, name: phonesName });
  const atPhoneCap = phoneFields.length >= MAX_PHONES_PER_PERSON;

  // ---------------------------------------------------------------------------
  // "Same as current" — delegate to useMirrorAddress hook
  // ---------------------------------------------------------------------------
  const { mailingSameAsCurrent, onSameAsCurrentChange } = useMirrorAddress({ control, personPath });

  // ---------------------------------------------------------------------------
  // "Email Not Available"
  // ---------------------------------------------------------------------------
  const emailNotAvailable = useWatch({
    control,
    name: getCustomerFieldPath(personPath, "emailNotAvailable"),
  });
  const { field: emailNotAvailableField } = useController({
    control,
    name: getCustomerFieldPath(personPath, "emailNotAvailable"),
  });
  const emailPath = getCustomerFieldPath(personPath, "email");
  const { field: emailField, fieldState: emailFieldState } = useController({
    control,
    name: emailPath,
  });
  // clearErrors lets us drop a stale email error when "Email Not Available" is
  // checked (RHF onBlur mode won't re-validate on the checkbox toggle alone).
  const { clearErrors } = useFormContext<SignersInput>();

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  function handleToggle() {
    setIsOpen((previous) => !previous);
  }

  function handleEmailNotAvailableChange(checked: boolean | "indeterminate") {
    const isChecked = checked === true;
    emailNotAvailableField.onChange(isChecked);
    if (isChecked) {
      // Clear the email value AND any stale validation error, then disable the
      // input (disabled is driven by emailNotAvailable in the render).
      emailField.onChange("");
      clearErrors(emailPath);
    }
  }

  function handleAddPhone() {
    if (!atPhoneCap) {
      append(makeEmptyPhone());
    }
  }

  function handleEmailBlur() {
    const current = emailField.value == null ? "" : String(emailField.value);
    const trimmed = current.trim();
    if (trimmed !== current) {
      emailField.onChange(trimmed);
    }
    emailField.onBlur();
  }

  // ---------------------------------------------------------------------------
  // Checkbox uid for a11y label association
  // ---------------------------------------------------------------------------
  const sameAsCurrentId = `${uid}-same-as-current`;
  const emailNotAvailableId = `${uid}-email-not-available`;
  const emailInputId = `${uid}-email`;
  const emailErrorId = `${uid}-email-error`;

  return (
    <div ref={residenceSectionRef}>
      {/* Section header */}
      <div className="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={handleToggle}
          className="flex flex-1 items-center justify-between gap-2 text-left text-sm font-medium"
        >
          <span>{t("review.customer.heading")}</span>
          <ChevronDownIcon
            className={cn(
              "text-muted-foreground size-4 shrink-0 transition-transform duration-200",
              isOpen ? "rotate-180" : "rotate-0"
            )}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Section content */}
      {isOpen && (
        <div id={contentId} className="flex flex-col gap-4 px-3 pb-4 pt-3">
          {/* ----------------------------------------------------------------
              A. Personal Details
          ---------------------------------------------------------------- */}
          <section aria-label={t("review.customer.sections.personalDetails")}>
            <h4 className="text-foreground mb-2 text-xs font-semibold tracking-wide">
              {t("review.customer.sections.personalDetails")}
            </h4>
            {/* 16-col grid: SSN(5) | Date of Birth(3) | Driver License(5) | Hold Coupon Book(3)
                + full-width (16) Name row — spans match the sm:grid-cols-16 design.

                SSN/ITIN, Date of Birth and the Name row are identity data owned
                upstream (credit application / CAPS) and are never editable here,
                so they are disabled unconditionally rather than via the VLA
                read-only lock. Driver License and Hold Coupon Book stay editable.
                SsnField reopens itself if its value fails validation — see there. */}
            <div className="sm:grid-cols-16 grid grid-cols-1 items-start gap-x-3 gap-y-3">
              <div className="sm:col-span-5">
                <SsnField control={control} personPath={personPath} disabled />
              </div>
              <div className="sm:col-span-3">
                <SimpleDateField
                  control={control}
                  fieldPath={getCustomerFieldPath(personPath, "dateOfBirth")}
                  label={t("review.customer.personal.dateOfBirth")}
                  required
                  disabled
                />
              </div>
              <div className="sm:col-span-5">
                <SimpleTextField
                  control={control}
                  fieldPath={getCustomerFieldPath(personPath, "driverLicense")}
                  label={t("review.customer.personal.driverLicense")}
                  autoComplete="off"
                />
              </div>
              <div className="sm:col-span-3">
                <SimpleSelectField
                  control={control}
                  fieldPath={getCustomerFieldPath(personPath, "holdCouponBook")}
                  label={t("review.customer.personal.holdCouponBook")}
                  options={HOLD_COUPON_BOOK_OPTIONS}
                />
              </div>

              {/* Name (full width): single label, placeholder-only First | MI | Last | Suffix */}
              <div className="sm:col-span-16">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium">
                    {t("review.customer.personal.name")}
                    <RequiredMarker />
                  </span>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                    <div className="flex-1">
                      <SimpleTextField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "firstName")}
                        label={t("review.customer.personal.firstName")}
                        placeholder={t("review.customer.personal.firstName")}
                        required
                        disabled
                        hideLabel
                        className="w-full"
                      />
                    </div>
                    <div className="w-full sm:w-12">
                      <SimpleTextField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "middleInitial")}
                        label={t("review.customer.personal.middleInitial")}
                        placeholder={t("review.customer.personal.middleInitial")}
                        disabled
                        hideLabel
                        className="w-full"
                      />
                    </div>
                    <div className="flex-1">
                      <SimpleTextField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "lastName")}
                        label={t("review.customer.personal.lastName")}
                        placeholder={t("review.customer.personal.lastName")}
                        required
                        disabled
                        hideLabel
                        className="w-full"
                      />
                    </div>
                    <div className="w-full sm:w-24">
                      <SimpleSelectField
                        control={control}
                        fieldPath={getCustomerFieldPath(personPath, "suffix")}
                        label={t("review.customer.personal.suffix")}
                        options={SUFFIX_OPTIONS}
                        placeholder={t("review.customer.personal.suffix")}
                        disabled
                        hideLabel
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ----------------------------------------------------------------
              B. Residence Information
          ---------------------------------------------------------------- */}
          <section aria-label={t("review.customer.sections.residenceInformation")}>
            <h4 className="text-foreground mb-2 text-xs font-semibold tracking-wide">
              {t("review.customer.sections.residenceInformation")}
            </h4>
            <div className="flex flex-col gap-3">
              {/* Physical Address */}
              <fieldset>
                <legend className="text-foreground mb-1 text-xs font-medium">
                  {t("review.customer.residence.physicalAddress")}
                </legend>
                <AddressFields
                  control={control}
                  streetNumberPath={getCustomerFieldPath(personPath, "physicalStreetNumber")}
                  streetNamePath={getCustomerFieldPath(personPath, "physicalStreetName")}
                  aptPath={getCustomerFieldPath(personPath, "physicalApt")}
                  zipPath={getCustomerFieldPath(personPath, "physicalZip")}
                  cityPath={getCustomerFieldPath(personPath, "physicalCity")}
                  statePath={getCustomerFieldPath(personPath, "physicalState")}
                  isPhysical
                />
              </fieldset>

              {/* Mailing Address */}
              <fieldset>
                {/* Mailing Address label with the "Same as current" checkbox inline */}
                <legend className="text-foreground mb-2 flex flex-wrap items-center gap-4 text-xs font-medium">
                  {t("review.customer.residence.mailingAddress")}
                  <span className="flex items-center gap-2">
                    <Checkbox
                      id={sameAsCurrentId}
                      checked={Boolean(mailingSameAsCurrent)}
                      onCheckedChange={onSameAsCurrentChange}
                    />
                    <label htmlFor={sameAsCurrentId} className="cursor-pointer text-xs font-normal">
                      {t("review.customer.residence.sameAsCurrent")}
                    </label>
                  </span>
                </legend>
                <AddressFields
                  control={control}
                  streetNumberPath={getCustomerFieldPath(personPath, "mailingStreetNumber")}
                  streetNamePath={getCustomerFieldPath(personPath, "mailingStreetName")}
                  aptPath={getCustomerFieldPath(personPath, "mailingApt")}
                  zipPath={getCustomerFieldPath(personPath, "mailingZip")}
                  cityPath={getCustomerFieldPath(personPath, "mailingCity")}
                  statePath={getCustomerFieldPath(personPath, "mailingState")}
                  disabled={Boolean(mailingSameAsCurrent)}
                />
              </fieldset>
            </div>
          </section>

          {/* ----------------------------------------------------------------
              C. Contact Information
          ---------------------------------------------------------------- */}
          <section aria-label={t("review.customer.sections.contactInformation")}>
            <h4 className="text-foreground mb-2 text-xs font-semibold tracking-wide">
              {t("review.customer.sections.contactInformation")}
            </h4>
            {/* Single stacked column: phone rows, then Add button, then email below */}
            <div className="flex flex-col gap-4">
              {/* Phone rows + Add link share a content-width column so the link
                  right-aligns to the widest row's edge (the delete-icon column). */}
              <div className="flex w-fit flex-col gap-3">
                {phoneFields.map((field, index) => (
                  <PhoneRow
                    key={field.id}
                    control={control}
                    personPath={personPath}
                    index={index}
                    onRemove={index > 0 ? () => remove(index) : undefined}
                  />
                ))}

                {/* Add phone number — subtle, always-underlined link aligned under the delete icon */}
                {!atPhoneCap && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={handleAddPhone}
                    className="text-muted-foreground text-2xs h-auto w-fit gap-1 self-end p-0 font-normal underline"
                  >
                    <PlusIcon className="size-3" aria-hidden="true" />
                    {t("review.customer.contact.addPhone")}
                  </Button>
                )}
              </div>

              {/* Email — horizontal (label-left), with Email Not Available beneath */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                  <label htmlFor={emailInputId} className="text-xs font-medium sm:w-32 sm:shrink-0">
                    {t("review.customer.contact.email")}
                    <RequiredMarker />
                  </label>
                  <div className="flex flex-col gap-1">
                    {/* Width aligns the input's right edge to the phone Type select:
                        number block (w-12 + w-12 + w-14 + 4×gap-1 + 2×dash w-2 = 11.5rem)
                        + gap-2 (0.5rem) + Type select w-32 (8rem) = 20rem → w-80. */}
                    <Input
                      id={emailInputId}
                      type="text"
                      value={emailField.value == null ? "" : String(emailField.value)}
                      onChange={emailField.onChange}
                      onBlur={handleEmailBlur}
                      ref={emailField.ref}
                      disabled={Boolean(emailNotAvailable)}
                      className="w-full sm:w-80"
                      aria-required
                      aria-invalid={Boolean(emailFieldState.error) || undefined}
                      aria-describedby={emailFieldState.error ? emailErrorId : undefined}
                    />
                    {emailFieldState.error && (
                      <p id={emailErrorId} className="text-destructive text-2xs" role="alert">
                        {emailFieldState.error.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email Not Available — below the email row, aligned under the input */}
                <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                  <span className="hidden sm:block sm:w-32 sm:shrink-0" aria-hidden="true" />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={emailNotAvailableId}
                      checked={Boolean(emailNotAvailable)}
                      onCheckedChange={handleEmailNotAvailableChange}
                    />
                    <label htmlFor={emailNotAvailableId} className="cursor-pointer text-xs font-normal">
                      {t("review.customer.contact.emailNotAvailable")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
